import { useCallback } from 'react'
import {
  useCreateCountryIdsVisibilityConditionMutation,
  useCreateDayOfWeekVisibilityConditionMutation,
  useCreateExpressMembershipVisibilityConditionMutation,
  useCreateExpressNonmemberLongTermHoldoutVisibilityConditionMutation,
  useCreateMatchesAnyRetailerIdVisibilityConditionMutation,
  useCreateMinimumPlatformVersionVisibilityConditionMutation,
  useCreateRetailerAvailabilityVisibilityConditionMutation,
  useCreateShoppedAtRetailerVisibilityConditionMutation,
  useCreateShoppedVisibilityConditionMutation,
  useCreateUserInteractionLimitVisibilityConditionMutation,
  useCreateWhitelistedPostalCodesVisibilityConditionMutation,
  useCreateWhitelistedZonesVisibilityConditionMutation,
  type CountryIdsInput,
  type DayOfWeekInput,
  type ExpressMembershipInput,
  type ExpressNonmemberLongTermHoldoutInput,
  type MatchesAnyRetailerIdInput,
  type MinimumPlatformVersionInput,
  type RetailerAvailabilityInput,
  type ShoppedAtRetailerInput,
  type ShoppedInput,
  type UserInteractionLimitInput,
  type WhitelistedPostalCodesInput,
  type WhitelistedZonesInput,
} from 'domains/banners/api'

interface CountryIdsVisibilityConditionArgs {
  visibilityConditionData: CountryIdsInput
}

export const useCreateCountryIdsVisibilityCondition = () => {
  const [triggerMutation, { data, loading, error, called }] =
    useCreateCountryIdsVisibilityConditionMutation()

  const createCountryIdsVisibilityCondition = useCallback(
    ({ visibilityConditionData }: CountryIdsVisibilityConditionArgs) =>
      triggerMutation({
        variables: {
          data: visibilityConditionData,
        },
      }),
    [triggerMutation]
  )

  return {
    createCountryIdsVisibilityCondition,
    apiCalled: called,
    apiResult: data?.countryIdsVisibilityConditionCreate.countryIdsVisibilityCondition,
    apiSuccess: Boolean(data),
    apiLoading: loading,
    apiError: error,
  }
}

interface DayOfWeekVisibilityConditionArgs {
  visibilityConditionData: DayOfWeekInput
}

export const useCreateDayOfWeekVisibilityCondition = () => {
  const [triggerMutation, { data, loading, error, called }] =
    useCreateDayOfWeekVisibilityConditionMutation()

  const createDayOfWeekVisibilityCondition = useCallback(
    ({ visibilityConditionData }: DayOfWeekVisibilityConditionArgs) =>
      triggerMutation({
        variables: {
          data: visibilityConditionData,
        },
      }),
    [triggerMutation]
  )

  return {
    createDayOfWeekVisibilityCondition,
    apiCalled: called,
    apiResult: data?.dayOfWeekVisibilityConditionCreate.dayOfWeekVisibilityCondition,
    apiSuccess: Boolean(data),
    apiLoading: loading,
    apiError: error,
  }
}

interface ExpressMembershipVisibilityConditionArgs {
  visibilityConditionData: ExpressMembershipInput
}

export const useCreateExpressMembershipVisibilityCondition = () => {
  const [triggerMutation, { data, loading, error, called }] =
    useCreateExpressMembershipVisibilityConditionMutation()

  const createExpressMembershipVisibilityCondition = useCallback(
    ({ visibilityConditionData }: ExpressMembershipVisibilityConditionArgs) =>
      triggerMutation({
        variables: {
          data: visibilityConditionData,
        },
      }),
    [triggerMutation]
  )

  return {
    createExpressMembershipVisibilityCondition,
    apiCalled: called,
    apiResult:
      data?.expressMembershipVisibilityConditionCreate.expressMembershipVisibilityCondition,
    apiSuccess: Boolean(data),
    apiLoading: loading,
    apiError: error,
  }
}

interface ExpressNonmemberLongTermHoldoutVisibilityConditionArgs {
  visibilityConditionData: ExpressNonmemberLongTermHoldoutInput
}

export const useCreateExpressNonmemberLongTermHoldoutVisibilityCondition = () => {
  const [triggerMutation, { data, loading, error, called }] =
    useCreateExpressNonmemberLongTermHoldoutVisibilityConditionMutation()

  const createExpressNonmemberLongTermHoldoutVisibilityCondition = useCallback(
    ({ visibilityConditionData }: ExpressNonmemberLongTermHoldoutVisibilityConditionArgs) =>
      triggerMutation({
        variables: {
          data: visibilityConditionData,
        },
      }),
    [triggerMutation]
  )

  return {
    createExpressNonmemberLongTermHoldoutVisibilityCondition,
    apiCalled: called,
    apiResult:
      data?.expressNonmemberLongTermHoldoutVisibilityConditionCreate
        .expressNonmemberLongTermHoldoutVisibilityCondition,
    apiSuccess: Boolean(data),
    apiLoading: loading,
    apiError: error,
  }
}

interface MatchesAnyRetailerIdVisibilityConditionArgs {
  visibilityConditionData: MatchesAnyRetailerIdInput
}

export const useCreateMatchesAnyRetailerIdVisibilityCondition = () => {
  const [triggerMutation, { data, loading, error, called }] =
    useCreateMatchesAnyRetailerIdVisibilityConditionMutation()

  const createMatchesAnyRetailerIdVisibilityCondition = useCallback(
    ({ visibilityConditionData }: MatchesAnyRetailerIdVisibilityConditionArgs) =>
      triggerMutation({
        variables: {
          data: visibilityConditionData,
        },
      }),
    [triggerMutation]
  )

  return {
    createMatchesAnyRetailerIdVisibilityCondition,
    apiCalled: called,
    apiResult:
      data?.matchesAnyRetailerIdVisibilityConditionCreate.matchesAnyRetailerIdVisibilityCondition,
    apiSuccess: Boolean(data),
    apiLoading: loading,
    apiError: error,
  }
}

interface MinimumPlatformVersionVisibilityConditionArgs {
  visibilityConditionData: MinimumPlatformVersionInput
}

export const useCreateMinimumPlatformVersionVisibilityCondition = () => {
  const [triggerMutation, { data, loading, error, called }] =
    useCreateMinimumPlatformVersionVisibilityConditionMutation()

  const createMinimumPlatformVersionVisibilityCondition = useCallback(
    ({ visibilityConditionData }: MinimumPlatformVersionVisibilityConditionArgs) =>
      triggerMutation({
        variables: {
          data: visibilityConditionData,
        },
      }),
    [triggerMutation]
  )

  return {
    createMinimumPlatformVersionVisibilityCondition,
    apiCalled: called,
    apiResult:
      data?.minimumPlatformVersionVisibilityConditionCreate
        .minimumPlatformVersionVisibilityCondition,
    apiSuccess: Boolean(data),
    apiLoading: loading,
    apiError: error,
  }
}

interface RetailerAvailabilityVisibilityConditionArgs {
  visibilityConditionData: RetailerAvailabilityInput
}

export const useCreateRetailerAvailabilityVisibilityCondition = () => {
  const [triggerMutation, { data, loading, error, called }] =
    useCreateRetailerAvailabilityVisibilityConditionMutation()

  const createRetailerAvailabilityVisibilityCondition = useCallback(
    ({ visibilityConditionData }: RetailerAvailabilityVisibilityConditionArgs) =>
      triggerMutation({
        variables: {
          data: visibilityConditionData,
        },
      }),
    [triggerMutation]
  )

  return {
    createRetailerAvailabilityVisibilityCondition,
    apiCalled: called,
    apiResult:
      data?.retailerAvailabilityVisibilityConditionCreate.retailerAvailabilityVisibilityCondition,
    apiSuccess: Boolean(data),
    apiLoading: loading,
    apiError: error,
  }
}

interface ShoppedAtRetailerVisibilityConditionArgs {
  visibilityConditionData: ShoppedAtRetailerInput
}

export const useCreateShoppedAtRetailerVisibilityCondition = () => {
  const [triggerMutation, { data, loading, error, called }] =
    useCreateShoppedAtRetailerVisibilityConditionMutation()

  const createShoppedAtRetailerVisibilityCondition = useCallback(
    ({ visibilityConditionData }: ShoppedAtRetailerVisibilityConditionArgs) =>
      triggerMutation({
        variables: {
          data: visibilityConditionData,
        },
      }),
    [triggerMutation]
  )

  return {
    createShoppedAtRetailerVisibilityCondition,
    apiCalled: called,
    apiResult:
      data?.shoppedAtRetailerVisibilityConditionCreate.shoppedAtRetailerVisibilityCondition,
    apiSuccess: Boolean(data),
    apiLoading: loading,
    apiError: error,
  }
}

interface ShoppedVisibilityConditionArgs {
  visibilityConditionData: ShoppedInput
}

export const useCreateShoppedVisibilityCondition = () => {
  const [triggerMutation, { data, loading, error, called }] =
    useCreateShoppedVisibilityConditionMutation()

  const createShoppedVisibilityCondition = useCallback(
    ({ visibilityConditionData }: ShoppedVisibilityConditionArgs) =>
      triggerMutation({
        variables: {
          data: visibilityConditionData,
        },
      }),
    [triggerMutation]
  )

  return {
    createShoppedVisibilityCondition,
    apiCalled: called,
    apiResult: data?.shoppedVisibilityConditionCreate.shoppedVisibilityCondition,
    apiSuccess: Boolean(data),
    apiLoading: loading,
    apiError: error,
  }
}

interface UserInteractionLimitVisibilityConditionArgs {
  visibilityConditionData: UserInteractionLimitInput
}

export const useCreateUserInteractionLimitVisibilityCondition = () => {
  const [triggerMutation, { data, loading, error, called }] =
    useCreateUserInteractionLimitVisibilityConditionMutation()

  const createUserInteractionLimitVisibilityCondition = useCallback(
    ({ visibilityConditionData }: UserInteractionLimitVisibilityConditionArgs) =>
      triggerMutation({
        variables: {
          data: visibilityConditionData,
        },
      }),
    [triggerMutation]
  )

  return {
    createUserInteractionLimitVisibilityCondition,
    apiCalled: called,
    apiResult:
      data?.userInteractionLimitVisibilityConditionCreate.userInteractionLimitVisibilityCondition,
    apiSuccess: Boolean(data),
    apiLoading: loading,
    apiError: error,
  }
}

interface WhitelistedPostalCodesVisibilityConditionArgs {
  visibilityConditionData: WhitelistedPostalCodesInput
}

export const useCreateWhitelistedPostalCodesVisibilityCondition = () => {
  const [triggerMutation, { data, loading, error, called }] =
    useCreateWhitelistedPostalCodesVisibilityConditionMutation()

  const createWhitelistedPostalCodesVisibilityCondition = useCallback(
    ({ visibilityConditionData }: WhitelistedPostalCodesVisibilityConditionArgs) =>
      triggerMutation({
        variables: {
          data: visibilityConditionData,
        },
      }),
    [triggerMutation]
  )

  return {
    createWhitelistedPostalCodesVisibilityCondition,
    apiCalled: called,
    apiResult:
      data?.whitelistedPostalCodesVisibilityConditionCreate
        .whitelistedPostalCodesVisibilityCondition,
    apiSuccess: Boolean(data),
    apiLoading: loading,
    apiError: error,
  }
}

interface WhitelistedZonesVisibilityConditionArgs {
  visibilityConditionData: WhitelistedZonesInput
}

export const useCreateWhitelistedZonesVisibilityCondition = () => {
  const [triggerMutation, { data, loading, error, called }] =
    useCreateWhitelistedZonesVisibilityConditionMutation()

  const createWhitelistedZonesVisibilityCondition = useCallback(
    ({ visibilityConditionData }: WhitelistedZonesVisibilityConditionArgs) =>
      triggerMutation({
        variables: {
          data: visibilityConditionData,
        },
      }),
    [triggerMutation]
  )

  return {
    createWhitelistedZonesVisibilityCondition,
    apiCalled: called,
    apiResult: data?.whitelistedZonesVisibilityConditionCreate.whitelistedZonesVisibilityCondition,
    apiSuccess: Boolean(data),
    apiLoading: loading,
    apiError: error,
  }
}
